import { CheckIcon } from 'lucide-react-native';
import type { ReactElement } from 'react';
import React from 'react';
import {
    Checkbox as TamaguiCheckbox,
    type CheckboxProps as TamaguiCheckboxProps,
    isWeb,
    Label,
    Separator,
    type StackProps,
    View,
    XStack,
    YStack,
} from 'tamagui';

import { hitSlop } from '../componentHelpers';
import {
    type ContainerComponent,
    InsetContainer,
} from '../containers/Containers';
import { Icon, type IconType } from '../icons/Icon';
import { Shadows } from '../Shadows';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
    LabelRegular,
} from '../text/Typography';

const getCheckColor = (disabled: boolean) => (disabled ? '$gray800' : '$white');
const getBorderColor = (
    disabled: boolean,
    isChecked: boolean,
    isPositive: boolean,
) => {
    if (disabled) return '$accent600';
    if (isPositive && isChecked) return '$green1000';
    return '$gray1200';
};
const getUncheckedBackgroundColor = (
    disabled: boolean,
    isChecked: boolean,
    isPositive: boolean,
) => {
    if (disabled) return '$accent400';
    if (isPositive && isChecked) return '$green900';
    if (isChecked) return '$accent900';
    return '$white';
};
const getCheckedBackgroundColor = (disabled: boolean, isPositive: boolean) => {
    if (disabled) return '$accent400';
    if (isPositive) return '$green900';
    return '$accent900';
};

export interface CheckboxProps extends Omit<
    TamaguiCheckboxProps,
    'size' | 'checked' | 'defaultChecked'
> {
    // exclude defaultChecked because this component is controlled, and that will only work for an uncontrolled component
    id: string;
    tone?: 'neutral' | 'positive';
    size?: 'default' | 'large'; // overwriting original tamagui checkbox type because we only want these sizes
    checked?: boolean; // overwriting original tamagui checkbox type because we do not use 'indeterminate' state
}

export const Checkbox = ({
    id,
    size = 'default',
    tone = 'neutral',
    disabled = false,
    checked,
    ...checkboxProps
}: CheckboxProps) => {
    const isChecked = checked === true;
    const isLarge = size === 'large';
    const isPositive = tone === 'positive';

    const checkColor = getCheckColor(disabled);
    const borderColor = getBorderColor(disabled, isChecked, isPositive);
    const uncheckedBackgroundColor = getUncheckedBackgroundColor(
        disabled,
        isChecked,
        isPositive,
    );
    const checkedBackgroundColor = getCheckedBackgroundColor(
        disabled,
        isPositive,
    );

    return (
        <TamaguiCheckbox
            id={id}
            checked={checked}
            disabled={disabled}
            unstyled
            height={isLarge ? 28 : 22}
            width={isLarge ? 28 : 22}
            borderRadius={isLarge ? 8 : 6}
            borderColor={borderColor}
            borderWidth={2}
            backgroundColor={uncheckedBackgroundColor}
            margin={isLarge ? 2 : 1}
            animation="quicker"
            animateOnly={[
                'backgroundColor',
                'borderColor',
                'boxShadow',
                'transform',
            ]}
            cursor={disabled ? 'default' : 'pointer'}
            pointerEvents={disabled ? 'none' : 'auto'}
            hoverStyle={isWeb && !disabled && { ...Shadows.RING }}
            focusStyle={isWeb && !disabled && { ...Shadows.RING }}
            pressStyle={{
                scale: 0.97,
                ...Shadows.RING_ACTIVE,
            }}
            {...hitSlop(4)}
            {...checkboxProps}
        >
            <TamaguiCheckbox.Indicator
                backgroundColor={checkedBackgroundColor}
                borderRadius={isLarge ? 5 : 3}
                width="100%"
                height="100%"
                alignItems="center"
                justifyContent="center"
            >
                <Icon
                    icon={CheckIcon}
                    size={isLarge ? 16 : 12}
                    color={checkColor}
                    marginTop={1}
                />
            </TamaguiCheckbox.Indicator>
        </TamaguiCheckbox>
    );
};

interface CheckboxWithLabelProps extends CheckboxProps {
    label: string;
}

export function CheckboxWithLabel({
    label,
    id,
    size,
    disabled,
    onPress,
    ...checkboxProps
}: CheckboxWithLabelProps) {
    const isLarge = size === 'large';
    return (
        <XStack
            alignItems="flex-start"
            justifyContent="flex-start"
            paddingVertical={2}
        >
            <Checkbox
                id={id}
                size={size}
                disabled={disabled}
                onPress={onPress}
                {...checkboxProps}
            />
            <Label
                onPress={onPress}
                fontWeight="$1"
                fontSize={isLarge ? 18 : 16}
                cursor={disabled ? 'default' : 'pointer'}
                color={disabled ? '$gray800' : '$black'}
                pointerEvents={disabled ? 'none' : undefined}
                lineHeight={isLarge ? 24 : 22}
                marginTop={isLarge ? 4 : 1}
                marginRight={6}
                paddingLeft={8}
            >
                {label}
            </Label>
        </XStack>
    );
}

interface CheckboxRowProps extends Omit<CheckboxProps, 'checked' | 'onPress'> {
    isSelected: boolean;
    onPress: () => void;
    label: string;
    labelIcon?: IconType;
    details?: string | ReactElement;
    detailsHiddenUntilSelected?: boolean;
    leftAlignOnWideScreen?: boolean;
    containerProps?: StackProps;
}

// DEPRECATED
export function LegacyCheckboxRow({
    id,
    label,
    labelIcon,
    isSelected,
    disabled = false,
    details,
    detailsHiddenUntilSelected = false,
    onPress,
    leftAlignOnWideScreen,
    containerProps,
    ...checkboxProps
}: CheckboxRowProps) {
    const textColor = disabled ? '$gray800' : '$black';
    const subLabelTextColor = disabled ? '$gray700' : '$gray900';
    const hoverBackgroundColor = disabled ? 'transparent' : '$gray100';

    const DetailsComponent: ContainerComponent = ({ children }) =>
        typeof children === 'string' ? (
            <BodySmallRegular color={subLabelTextColor}>
                {children}
            </BodySmallRegular>
        ) : (
            <XStack
                width="100%"
                onPress={(e) => {
                    e.stopPropagation();
                }}
            >
                {children}
            </XStack>
        );

    const CheckboxComponent = () => (
        <Checkbox
            id={id}
            disabled={disabled}
            checked={isSelected}
            onPress={(e) => {
                onPress();
                e.stopPropagation();
            }}
            {...checkboxProps}
        />
    );

    const showDetails =
        details != null && (detailsHiddenUntilSelected ? isSelected : true);
    return (
        <XStack
            width="100%"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={16}
            paddingHorizontal={isWeb ? 16 : 8}
            paddingVertical={16}
            borderWidth={1}
            borderRadius={12}
            borderColor="$white"
            backgroundColor="$white"
            onPress={onPress}
            cursor={disabled ? 'default' : 'pointer'}
            pointerEvents={disabled ? 'none' : undefined}
            animation="quick"
            animateOnly={['backgroundColor']}
            hoverStyle={{
                backgroundColor: hoverBackgroundColor,
            }}
            opacity={1}
            pressStyle={
                !disabled && {
                    opacity: 0.6,
                }
            }
            {...containerProps}
        >
            {leftAlignOnWideScreen && (
                <View
                    display="none"
                    paddingRight={16}
                    $sm={{ display: 'flex' }}
                >
                    <CheckboxComponent />
                </View>
            )}
            {labelIcon && (
                <Icon
                    icon={labelIcon}
                    color={textColor}
                    size={16}
                    marginTop={4 /* minor adjustment for vertical alignment */}
                />
            )}
            <YStack
                flex={1}
                marginTop={1 /* minor adjustment for vertical alignment */}
            >
                <BodyMediumEmphasized color={textColor} flex={1}>
                    {label}
                </BodyMediumEmphasized>
                {showDetails && <DetailsComponent>{details}</DetailsComponent>}
            </YStack>
            <View $sm={leftAlignOnWideScreen && { display: 'none' }}>
                <CheckboxComponent />
            </View>
        </XStack>
    );
}

interface CheckboxRowListProps {
    items: readonly CheckboxRowProps[];
    hasSeparators?: boolean;
}

export const CheckboxRowList = ({
    items,
    hasSeparators = true,
}: CheckboxRowListProps) => {
    return (
        <YStack width="100%">
            {items.map((props, index) => (
                <React.Fragment key={index}>
                    <LegacyCheckboxRow {...props} />
                    {hasSeparators && index !== items.length - 1 && (
                        <Separator marginHorizontal={16} marginVertical={2} />
                    )}
                </React.Fragment>
            ))}
        </YStack>
    );
};

interface CheckboxRowInsetListProps extends CheckboxRowListProps {
    title?: string;
}

export const CheckboxRowInsetList = ({
    title,
    items,
    hasSeparators = true,
}: CheckboxRowInsetListProps) => {
    return (
        <YStack width="100%">
            {title != null && (
                <LabelRegular
                    marginBottom={10}
                    marginLeft={8}
                    color="$gray1000"
                >
                    {title}
                </LabelRegular>
            )}
            <InsetContainer padding={4}>
                {items.map((checkboxRowProps, index) => (
                    <React.Fragment key={index}>
                        <LegacyCheckboxRow
                            {...checkboxRowProps}
                            containerProps={{
                                paddingHorizontal: 16,
                                ...checkboxRowProps.containerProps,
                            }}
                        />
                        {hasSeparators && index !== items.length - 1 && (
                            <Separator
                                marginHorizontal={16}
                                marginVertical={2}
                            />
                        )}
                    </React.Fragment>
                ))}
            </InsetContainer>
        </YStack>
    );
};
